import React, { useContext, useEffect, useMemo, useState } from "react";

import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	makeStyles,
	MenuItem,
	Radio,
	RadioGroup,
	Switch,
	TextField,
	Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatDuration } from "../Dashboard/format";

const LIMITS = { minDelay: 15, maxDelay: 900, minHourly: 1, maxHourly: 120 };

const useStyles = makeStyles(theme => ({
	section: {
		margin: theme.spacing(2, 0, 1),
	},
	row: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(2),
	},
	field: {
		flex: "1 1 140px",
		minWidth: 120,
	},
	hint: {
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
	},
	preview: {
		whiteSpace: "pre-wrap",
		padding: theme.spacing(1.5),
		marginTop: theme.spacing(1),
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.action.hover,
	},
	summary: {
		display: "grid",
		gap: theme.spacing(0.5),
		margin: theme.spacing(1, 0, 2),
	},
}));

const initialForm = () => ({
	name: "",
	whatsappId: "",
	audienceType: "all",
	contacts: [],
	numbers: "",
	tagId: "",
	message: "",
	minDelaySeconds: 30,
	maxDelaySeconds: 90,
	hourlyLimit: 40,
	useWindow: true,
	windowStart: "09:00",
	windowEnd: "18:00",
	optOutNotice: true,
	scheduledAt: "",
});

// Sample text for the preview: first option of each {a|b}, example name.
const previewText = (form, notice) => {
	let text = form.message;
	for (let i = 0; i < 20 && /\{[^{}]*\|[^{}]*\}/.test(text); i += 1) {
		text = text.replace(/\{([^{}]*\|[^{}]*)\}/, (_m, inner) => inner.split("|")[0]);
	}
	text = text
		.replace(/\{\{\s*firstName\s*\}\}/g, "Maria")
		.replace(/\{\{\s*name\s*\}\}/g, "Maria Souza");
	return form.optOutNotice ? `${text}\n\n${notice}` : text;
};

const CampaignModal = ({ open, onClose, onChanged }) => {
	const classes = useStyles();
	const { whatsApps } = useContext(WhatsAppsContext);

	const [form, setForm] = useState(initialForm);
	const [step, setStep] = useState("form"); // form | review
	const [created, setCreated] = useState(null);
	const [saving, setSaving] = useState(false);
	const [contactOptions, setContactOptions] = useState([]);
	const [contactSearch, setContactSearch] = useState("");
	const [tags, setTags] = useState([]);

	const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

	useEffect(() => {
		if (!open) return;
		setForm(initialForm());
		setStep("form");
		setCreated(null);
	}, [open]);

	useEffect(() => {
		if (!open || form.whatsappId || !whatsApps || whatsApps.length === 0) return;
		const preferred =
			whatsApps.find(w => w.isDefault && w.status === "CONNECTED") ||
			whatsApps.find(w => w.status === "CONNECTED") ||
			whatsApps[0];
		set("whatsappId", preferred.id);
	}, [open, whatsApps, form.whatsappId]);

	useEffect(() => {
		if (!open || form.audienceType !== "tag") return;
		api
			.get("/tags")
			.then(({ data }) => setTags(data.tags))
			.catch(toastError);
	}, [open, form.audienceType]);

	useEffect(() => {
		if (!open || form.audienceType !== "contacts") return undefined;
		const timer = setTimeout(async () => {
			try {
				const { data } = await api.get("/contacts", {
					params: { searchParam: contactSearch, pageNumber: 1 },
				});
				setContactOptions(
					data.contacts.filter(c => !c.isGroup && c.number && !c.campaignOptOut)
				);
			} catch (err) {
				toastError(err);
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [open, form.audienceType, contactSearch]);

	const selectedWhatsapp = useMemo(
		() => (whatsApps || []).find(w => w.id === form.whatsappId),
		[whatsApps, form.whatsappId]
	);

	const notice = i18n.t("campaigns.optOutNotice");

	const errors = useMemo(() => {
		const e = {};
		const min = Number(form.minDelaySeconds);
		const max = Number(form.maxDelaySeconds);
		const hourly = Number(form.hourlyLimit);

		if (!form.name.trim()) e.name = true;
		if (!form.whatsappId) e.whatsappId = true;
		if (!form.message.trim()) e.message = true;
		if (!Number.isInteger(min) || min < LIMITS.minDelay) e.minDelaySeconds = true;
		if (!Number.isInteger(max) || max < min || max > LIMITS.maxDelay) e.maxDelaySeconds = true;
		if (!Number.isInteger(hourly) || hourly < LIMITS.minHourly || hourly > LIMITS.maxHourly) {
			e.hourlyLimit = true;
		}
		if (form.useWindow && (!form.windowStart || !form.windowEnd || form.windowStart === form.windowEnd)) {
			e.window = true;
		}
		if (form.audienceType === "contacts" && form.contacts.length === 0) e.audience = true;
		if (form.audienceType === "numbers" && !form.numbers.trim()) e.audience = true;
		if (form.audienceType === "tag" && !form.tagId) e.audience = true;
		return e;
	}, [form]);

	const hasErrors = Object.keys(errors).length > 0;

	const audiencePayload = () => {
		if (form.audienceType === "contacts") {
			return { type: "contacts", contactIds: form.contacts.map(c => c.id) };
		}
		if (form.audienceType === "numbers") {
			return { type: "numbers", numbers: form.numbers };
		}
		if (form.audienceType === "tag") {
			return { type: "tag", tagId: Number(form.tagId) };
		}
		return { type: "all" };
	};

	const handleCreate = async () => {
		setSaving(true);
		try {
			const { data } = await api.post("/campaigns", {
				name: form.name.trim(),
				message: form.message,
				whatsappId: Number(form.whatsappId),
				audience: audiencePayload(),
				minDelaySeconds: Number(form.minDelaySeconds),
				maxDelaySeconds: Number(form.maxDelaySeconds),
				hourlyLimit: Number(form.hourlyLimit),
				windowStart: form.useWindow ? form.windowStart : null,
				windowEnd: form.useWindow ? form.windowEnd : null,
				utcOffsetMinutes: new Date().getTimezoneOffset(),
				optOutNotice: form.optOutNotice,
				scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
			});
			setCreated(data);
			setStep("review");
			onChanged();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleStart = async () => {
		setSaving(true);
		try {
			await api.post(`/campaigns/${created.campaign.id}/start`);
			toast.success(i18n.t("campaigns.toasts.started"));
			onChanged();
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const summary = created && created.summary;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>
				{step === "form"
					? i18n.t("campaigns.modal.title")
					: i18n.t("campaigns.modal.reviewTitle")}
			</DialogTitle>

			{step === "form" && (
				<DialogContent dividers>
					<TextField
						label={i18n.t("campaigns.modal.name")}
						value={form.name}
						onChange={e => set("name", e.target.value)}
						error={!!errors.name && form.name !== ""}
						variant="outlined"
						margin="dense"
						fullWidth
						inputProps={{ maxLength: 191 }}
					/>

					<TextField
						select
						label={i18n.t("campaigns.modal.connection")}
						value={form.whatsappId}
						onChange={e => set("whatsappId", e.target.value)}
						variant="outlined"
						margin="dense"
						fullWidth
						helperText={
							selectedWhatsapp && selectedWhatsapp.status !== "CONNECTED"
								? i18n.t("campaigns.modal.connectionOffline")
								: ""
						}
					>
						{(whatsApps || []).map(w => (
							<MenuItem key={w.id} value={w.id}>
								{w.name} ({w.status})
							</MenuItem>
						))}
					</TextField>

					<Typography variant="subtitle2" className={classes.section}>
						{i18n.t("campaigns.modal.audience")}
					</Typography>
					<RadioGroup
						value={form.audienceType}
						onChange={e => set("audienceType", e.target.value)}
					>
						<FormControlLabel value="all" control={<Radio />} label={i18n.t("campaigns.modal.audienceAll")} />
						<FormControlLabel value="contacts" control={<Radio />} label={i18n.t("campaigns.modal.audienceContacts")} />
						<FormControlLabel value="tag" control={<Radio />} label={i18n.t("campaigns.modal.audienceTag")} />
						<FormControlLabel value="numbers" control={<Radio />} label={i18n.t("campaigns.modal.audienceNumbers")} />
					</RadioGroup>

					{form.audienceType === "contacts" && (
						<Autocomplete
							multiple
							options={contactOptions}
							value={form.contacts}
							getOptionLabel={c => `${c.name} (${c.number})`}
							getOptionSelected={(a, b) => a.id === b.id}
							filterOptions={x => x}
							onInputChange={(_e, value, reason) => {
								if (reason === "input") setContactSearch(value);
							}}
							onChange={(_e, value) => set("contacts", value)}
							noOptionsText={i18n.t("campaigns.modal.noContacts")}
							renderInput={params => (
								<TextField
									{...params}
									variant="outlined"
									margin="dense"
									label={i18n.t("campaigns.modal.searchContacts")}
								/>
							)}
						/>
					)}

					{form.audienceType === "tag" && (
						<TextField
							select
							variant="outlined"
							margin="dense"
							fullWidth
							label={i18n.t("campaigns.modal.chooseTag")}
							value={form.tagId}
							onChange={e => set("tagId", e.target.value)}
							helperText={tags.length === 0 ? i18n.t("campaigns.modal.noTags") : ""}
						>
							{tags.map(tag => (
								<MenuItem key={tag.id} value={tag.id}>
									{tag.name} ({i18n.t("tags.contactsCount", { count: tag.contacts })})
								</MenuItem>
							))}
						</TextField>
					)}

					{form.audienceType === "numbers" && (
						<>
							<TextField
								multiline
								rows={5}
								variant="outlined"
								margin="dense"
								fullWidth
								value={form.numbers}
								onChange={e => set("numbers", e.target.value)}
								placeholder={"11 91234-5678\nMaria;21 98888-7777"}
							/>
							<Typography variant="caption" className={classes.hint}>
								{i18n.t("campaigns.modal.numbersHint")}
							</Typography>
						</>
					)}

					<Typography variant="subtitle2" className={classes.section}>
						{i18n.t("campaigns.modal.message")}
					</Typography>
					<TextField
						multiline
						rows={6}
						variant="outlined"
						margin="dense"
						fullWidth
						value={form.message}
						onChange={e => set("message", e.target.value)}
						inputProps={{ maxLength: 3000 }}
						helperText={`${form.message.length}/3000 · ${i18n
							.t("campaigns.modal.messageHint")
							.replace(/\[\[(\w+)\]\]/g, "{{$1}}")}`}
					/>
					{form.message.trim() && (
						<div className={classes.preview}>
							<Typography variant="caption" className={classes.hint}>
								{i18n.t("campaigns.modal.preview")}
							</Typography>
							<div>{previewText(form, notice)}</div>
						</div>
					)}
					<FormControlLabel
						control={
							<Switch
								checked={form.optOutNotice}
								onChange={e => set("optOutNotice", e.target.checked)}
								color="primary"
							/>
						}
						label={i18n.t("campaigns.modal.optOutNotice")}
					/>

					<Typography variant="subtitle2" className={classes.section}>
						{i18n.t("campaigns.modal.pace")}
					</Typography>
					<div className={classes.row}>
						<TextField
							className={classes.field}
							type="number"
							variant="outlined"
							margin="dense"
							label={i18n.t("campaigns.modal.minDelay")}
							value={form.minDelaySeconds}
							onChange={e => set("minDelaySeconds", e.target.value)}
							error={!!errors.minDelaySeconds}
							inputProps={{ min: LIMITS.minDelay }}
						/>
						<TextField
							className={classes.field}
							type="number"
							variant="outlined"
							margin="dense"
							label={i18n.t("campaigns.modal.maxDelay")}
							value={form.maxDelaySeconds}
							onChange={e => set("maxDelaySeconds", e.target.value)}
							error={!!errors.maxDelaySeconds}
							inputProps={{ min: LIMITS.minDelay, max: LIMITS.maxDelay }}
						/>
						<TextField
							className={classes.field}
							type="number"
							variant="outlined"
							margin="dense"
							label={i18n.t("campaigns.modal.hourlyLimit")}
							value={form.hourlyLimit}
							onChange={e => set("hourlyLimit", e.target.value)}
							error={!!errors.hourlyLimit}
							inputProps={{ min: LIMITS.minHourly, max: LIMITS.maxHourly }}
						/>
					</div>
					<Typography variant="caption" className={classes.hint}>
						{i18n.t("campaigns.modal.paceHint", { min: LIMITS.minDelay })}
					</Typography>

					<FormControlLabel
						style={{ display: "flex", marginTop: 8 }}
						control={
							<Switch
								checked={form.useWindow}
								onChange={e => set("useWindow", e.target.checked)}
								color="primary"
							/>
						}
						label={i18n.t("campaigns.modal.useWindow")}
					/>
					{form.useWindow && (
						<div className={classes.row}>
							<TextField
								className={classes.field}
								type="time"
								variant="outlined"
								margin="dense"
								label={i18n.t("campaigns.modal.windowStart")}
								value={form.windowStart}
								onChange={e => set("windowStart", e.target.value)}
								error={!!errors.window}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								className={classes.field}
								type="time"
								variant="outlined"
								margin="dense"
								label={i18n.t("campaigns.modal.windowEnd")}
								value={form.windowEnd}
								onChange={e => set("windowEnd", e.target.value)}
								error={!!errors.window}
								InputLabelProps={{ shrink: true }}
							/>
						</div>
					)}

					<TextField
						type="datetime-local"
						variant="outlined"
						margin="dense"
						fullWidth
						label={i18n.t("campaigns.modal.scheduledAt")}
						value={form.scheduledAt}
						onChange={e => set("scheduledAt", e.target.value)}
						InputLabelProps={{ shrink: true }}
						helperText={i18n.t("campaigns.modal.scheduledHint")}
					/>
				</DialogContent>
			)}

			{step === "review" && summary && (
				<DialogContent dividers>
					<Typography variant="body1" gutterBottom>
						{i18n.t("campaigns.review.ready", { count: summary.total })}
					</Typography>
					<div className={classes.summary}>
						{summary.optOut > 0 && (
							<Typography variant="body2">
								{i18n.t("campaigns.review.optOut", { count: summary.optOut })}
							</Typography>
						)}
						{summary.invalid > 0 && (
							<Typography variant="body2">
								{i18n.t("campaigns.review.invalid", { count: summary.invalid })}
							</Typography>
						)}
						{summary.duplicates > 0 && (
							<Typography variant="body2">
								{i18n.t("campaigns.review.duplicates", { count: summary.duplicates })}
							</Typography>
						)}
						<Typography variant="body2">
							{i18n.t("campaigns.review.estimate", {
								time: formatDuration(created.campaign.estimatedSecondsRemaining),
							})}
						</Typography>
					</div>
					<Typography variant="subtitle2">
						{i18n.t("campaigns.modal.preview")}
					</Typography>
					<div className={classes.preview}>{previewText(form, notice)}</div>
					<Typography variant="body2" className={classes.hint}>
						{i18n.t("campaigns.review.warning")}
					</Typography>
				</DialogContent>
			)}

			<DialogActions>
				{step === "form" ? (
					<>
						<Button onClick={onClose} disabled={saving}>
							{i18n.t("campaigns.buttons.close")}
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={handleCreate}
							disabled={hasErrors || saving}
						>
							{saving ? <CircularProgress size={20} /> : i18n.t("campaigns.buttons.review")}
						</Button>
					</>
				) : (
					<>
						<Button onClick={onClose} disabled={saving}>
							{i18n.t("campaigns.buttons.keepDraft")}
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={handleStart}
							disabled={saving}
						>
							{saving ? <CircularProgress size={20} /> : i18n.t("campaigns.buttons.start")}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default CampaignModal;
